import {
  OkResponse,
  type Product,
  Variant,
  type Variant as VariantDto,
  formatBRL,
  variantSkuOf,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Field } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { Drawer, Modal } from '../../components/Overlays';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { formatGrams, formatHm } from '../../lib/numbers';
import { useCostPreview, useInvalidateProducts } from '../../lib/products-api';
import { useDebounced } from '../../lib/use-debounced';
import { useProductFiles } from './FilesTab';
import {
  type ProfileForm,
  ProfileEditor,
  batchLine,
  emptyPlate,
  formOf,
  profileOf,
} from './ProfileEditor';

const SIZES = [
  ['P', 'P · Pequeno'],
  ['M', 'M · Médio'],
  ['G', 'G · Grande'],
  ['GG', 'GG · Extra grande'],
] as const;

function NewVariantDialog({
  product,
  onClose,
  onCreated,
}: {
  product: Product;
  onClose: () => void;
  onCreated: (v: VariantDto) => void;
}) {
  const invalidate = useInvalidateProducts();
  const toast = useToast();
  const [key] = useState(newIdempotencyKey);
  const [size, setSize] = useState('M');
  const [color, setColor] = useState('');
  const short = variantSkuOf(product.sku, size || null, null);
  const sku =
    size && !product.variants.some((v) => v.sku === short)
      ? short
      : variantSkuOf(product.sku, size || null, color.trim() || null);
  const taken = product.variants.some((v) => v.sku === sku);
  const name = [size, color.trim()].filter(Boolean).join(' · ') || 'Única';
  const create = useMutation({
    mutationFn: () => {
      const base = product.variants[0]?.profile;
      return api.post(
        `/products/${product.id}/variants`,
        Variant,
        {
          name,
          size: size || null,
          color: color.trim() || null,
          // Start from the first variant's printers, kit and labor (grams and time are per variant).
          profile: base ? { ...base, plates: [], supplies: base.supplies } : undefined,
        },
        { idempotencyKey: key },
      );
    },
    onSuccess: (v) => {
      invalidate();
      toast(`Variação ${v.name} criada · preencha o perfil`);
      onCreated(v);
    },
    onError: (err) => toast(errorMessage(err)),
  });
  return (
    <Modal
      title="Nova variação"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={taken || create.isPending}
            onClick={() => create.mutate()}
          >
            Criar variação
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field id="nv-size" label="Tamanho">
          <select
            id="nv-size"
            className="input"
            value={size}
            onChange={(e) => setSize(e.target.value)}
          >
            {SIZES.map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
            <option value="">Sem tamanho</option>
          </select>
        </Field>
        <Field id="nv-color" label="Cor">
          <input
            id="nv-color"
            className="input"
            placeholder="Ex.: Coral"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
        </Field>
      </div>
      <div className="kv">
        <span className="muted">SKU</span>
        <span className="v tab">{sku}</span>
      </div>
      {taken ? <p className="help err">Já existe uma variação {sku}.</p> : null}
      <p className="xs muted">
        Depois de criar, abra a variação para preencher placas, gramas, tempo e materiais.
      </p>
    </Modal>
  );
}

function VariantDrawer({ variant, onClose }: { variant: VariantDto; onClose: () => void }) {
  const invalidate = useInvalidateProducts();
  const toast = useToast();
  const [name, setName] = useState(variant.name);
  const [sku, setSku] = useState(variant.sku);
  const [form, setForm] = useState<ProfileForm>(() => {
    const f = formOf(variant.profile);
    return f.plates.length ? f : { ...f, plates: [emptyPlate()] };
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sliced, setSliced] = useState<string | null>(variant.slicedVersionId);
  const productFiles = useProductFiles(variant.productId);
  const slicedFiles = (productFiles.data?.data ?? []).filter((f) => f.group === 'sliced');
  const applySliced = useMutation({
    mutationFn: (productFileId: string) =>
      api.post(`/variants/${variant.id}/apply-sliced`, Variant, { productFileId }),
    onSuccess: (v) => {
      invalidate();
      setForm(formOf(v.profile));
      setSliced(v.slicedVersionId);
      toast(`Gramas e tempo de ${v.name} vieram do arquivo`);
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const profile = profileOf(form);
  const preview = useCostPreview(useDebounced(profile, 300));
  const live = preview.data?.cost;
  const saved = variant.cost?.unitCostCentavos;
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/variants/${variant.id}`, Variant, {
        name: name.trim() || variant.name,
        ...(sku.trim() && sku.trim() !== variant.sku ? { sku: sku.trim() } : {}),
        profile,
        slicedVersionId: sliced,
      }),
    onSuccess: (v) => {
      invalidate();
      toast(
        `Perfil de ${v.name} salvo${v.cost ? ` · custo ${formatBRL(v.cost.unitCostCentavos)}` : ''}`,
      );
      onClose();
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/variants/${variant.id}`, OkResponse),
    onSuccess: () => {
      invalidate();
      toast(`Variação ${variant.name} excluída`);
      onClose();
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const delta = live && saved !== undefined ? live.unitCostCentavos - saved : null;

  return (
    <Drawer title={`Variação ${variant.name}`} kicker={variant.sku} onClose={onClose}>
      <div className="col" style={{ gap: 18, overflowY: 'auto', flex: 1, paddingBottom: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 10 }}>
          <Field id="vd-name" label="Nome">
            <input
              id="vd-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field id="vd-sku" label="SKU">
            <input
              id="vd-sku"
              className="input tab"
              value={sku}
              onChange={(e) => setSku(e.target.value.toUpperCase())}
            />
          </Field>
        </div>
        <Field label="Arquivo fatiado" help="A versão de onde vieram gramas e tempo.">
          <div className="row wrap" style={{ gap: 8 }}>
            {slicedFiles.map((f) => (
              <button
                key={f.id}
                type="button"
                className={sliced === f.id ? 'chip on' : 'chip'}
                aria-pressed={sliced === f.id}
                onClick={() => setSliced(f.id)}
              >
                {f.name.replace(/\.gcode\.3mf$|\.gcode$|\.3mf$/i, '')} v{f.version}
                {f.isCurrent ? ' · atual' : ''}
              </button>
            ))}
            <button
              type="button"
              className={sliced === null ? 'chip on' : 'chip'}
              aria-pressed={sliced === null}
              onClick={() => setSliced(null)}
            >
              Sem arquivo
            </button>
          </div>
          {slicedFiles.length === 0 ? (
            <span className="xs muted">
              Envie o .gcode.3mf na aba Arquivos para ligar a variação ao fatiado.
            </span>
          ) : null}
          {sliced ? (
            <button
              type="button"
              className="btn sm"
              style={{ alignSelf: 'flex-start' }}
              disabled={applySliced.isPending}
              onClick={() => applySliced.mutate(sliced)}
            >
              Usar gramas e tempo deste arquivo
            </button>
          ) : null}
        </Field>
        <ProfileEditor form={form} update={(fn) => setForm((f) => fn(f))} />
        <div className="card sunk col" style={{ gap: 4, padding: 14 }}>
          <span className="lbl">Custo unitário</span>
          <span className="big">{live ? formatBRL(live.unitCostCentavos) : '—'}</span>
          {live ? (
            <>
              <span className="xs muted">
                {saved === undefined
                  ? 'Ainda sem custo salvo'
                  : delta === null || delta === 0
                    ? 'Igual ao custo salvo'
                    : `${delta > 0 ? '+' : '−'} ${formatBRL(Math.abs(delta))} em relação ao salvo (${formatBRL(saved ?? 0)})`}
              </span>
              <span className="xs muted">{batchLine(live)}</span>
            </>
          ) : (
            <span className="xs muted">{preview.data?.missing ?? 'Calculando…'}</span>
          )}
        </div>
      </div>
      <div
        className="row between"
        style={{ gap: 10, paddingTop: 12, borderTop: '1px solid var(--line)' }}
      >
        <button
          type="button"
          className="btn g"
          style={{ color: 'var(--err)' }}
          onClick={() => setConfirmDelete(true)}
        >
          Excluir variação
        </button>
        <div className="row" style={{ gap: 10 }}>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar perfil
          </button>
        </div>
      </div>
      {confirmDelete ? (
        <Modal
          title={`Excluir a variação ${variant.name}?`}
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirmDelete(false)}>
                Cancelar
              </button>
              <button type="button" className="btn d" onClick={() => remove.mutate()}>
                Excluir
              </button>
            </>
          }
        >
          <p className="sm">
            Vai para a lixeira. Orçamentos já feitos guardam os números congelados.
          </p>
        </Modal>
      ) : null}
    </Drawer>
  );
}

/** "Variações" tab (board Produto-Visao, Variações). */
export function VariantsTab({ product }: { product: Product }) {
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const opened = product.variants.find((v) => v.id === open);
  const cols = '1.1fr 1.1fr .9fr .8fr .9fr .8fr 1.1fr';
  return (
    <section className="card flat">
      <div className="row between wrap" style={{ padding: '16px 18px 6px', gap: 10 }}>
        <div>
          <h2>Variações</h2>
          <p className="xs muted">
            Toque numa linha para ver o perfil de impressão e a lista de materiais.
          </p>
        </div>
        <button type="button" className="btn sm p" onClick={() => setCreating(true)}>
          <Icon name="plus" size={16} />
          Variação
        </button>
      </div>
      {product.variants.length === 0 ? (
        <div className="empty">
          <div className="emptyart" />
          <h2>Nenhuma variação ainda</h2>
          <p>
            Cada tamanho ou cor com perfil de impressão próprio vira uma variação com SKU e custo.
          </p>
          <button type="button" className="btn p" onClick={() => setCreating(true)}>
            Criar variação
          </button>
        </div>
      ) : (
        <div className="gt" style={{ overflowX: 'auto' }}>
          <div className="gtr head" style={{ gridTemplateColumns: cols, minWidth: 640 }}>
            <span>Variação</span>
            <span>SKU</span>
            <span>Cor</span>
            <span>Tamanho</span>
            <span>Material</span>
            <span className="num">Pronta entrega</span>
            <span className="num">Custo un.</span>
          </div>
          {product.variants.map((v) => (
            <button
              key={v.id}
              type="button"
              className="gtr"
              style={{ gridTemplateColumns: cols, minWidth: 640 }}
              aria-label={`Abrir perfil de ${v.name}`}
              onClick={() => setOpen(v.id)}
            >
              <span style={{ fontWeight: 500 }}>{v.name}</span>
              <span className="tab sm">{v.sku}</span>
              <span className="row" style={{ gap: 6 }}>
                {v.colorHex ? (
                  <span
                    className="dot"
                    style={{ background: v.colorHex, border: '1px solid rgba(0,0,0,.15)' }}
                  />
                ) : null}
                {v.color ?? '—'}
              </span>
              <span>{v.size ?? '—'}</span>
              <span className="sm">{v.material ?? '—'}</span>
              <span className="num">
                {v.stock.onHand ? `${v.stock.onHand} un.` : '—'}
                {v.stock.reserved ? (
                  <span className="xs muted" style={{ display: 'block' }}>
                    {v.stock.reserved} reserv.
                  </span>
                ) : null}
              </span>
              <span className="num">
                {v.cost ? (
                  <>
                    <b style={{ fontWeight: 600 }}>{formatBRL(v.cost.unitCostCentavos)}</b>
                    <br />
                    <span className="xs muted">
                      {formatGrams(v.cost.perUnit.grams)} · {formatHm(v.cost.perUnit.printSeconds)}
                    </span>
                  </>
                ) : (
                  <>
                    —<br />
                    <span className="xs muted">perfil pendente</span>
                  </>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
      {creating ? (
        <NewVariantDialog
          product={product}
          onClose={() => setCreating(false)}
          onCreated={(v) => {
            setCreating(false);
            setOpen(v.id);
          }}
        />
      ) : null}
      {opened ? (
        <VariantDrawer key={opened.id} variant={opened} onClose={() => setOpen(null)} />
      ) : null}
    </section>
  );
}
